import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { ServerAuthUrlPipe } from 'auth';
import { I18nService, TranslationModule } from 'i18n';
import { combineLatest, map, switchMap } from 'rxjs';
import { CatalogCourse } from './program-catalog.models';
import { ProgramCatalogService } from './program-catalog.service';

/**
 * Course detail + buy page for a NOT-yet-owned course. Shows the description in
 * the spirit of the programs landing sub-pages, and a single button that adds the
 * course product to the order-wizard cart and jumps straight to the payment step
 * (via /order-wizard?add=<productId>&step=2). If the course is already accessible
 * (free/owned), it redirects to the content viewer instead.
 */
@Component({
  selector: 'app-course-detail-page',
  standalone: true,
  imports: [CommonModule, TranslationModule, ServerAuthUrlPipe],
  templateUrl: './course-detail-page.component.html',
  styleUrls: ['./course-detail-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CourseDetailPageComponent {
  private catalogService = inject(ProgramCatalogService);
  private i18n = inject(I18nService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);

  course = signal<CatalogCourse | null>(null);
  loading = signal(true);
  notFound = signal(false);

  constructor() {
    combineLatest([this.route.paramMap, this.i18n.currentLanguage$])
      .pipe(
        switchMap(([params, lang]) =>
          this.catalogService.getCatalog(lang).pipe(
            map(res => ({ res, programId: params.get('programId') }))
          )
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: ({ res, programId }) => {
          const found = res.categories
            .flatMap(c => c.courses)
            .find(c => c.program_id === programId) || null;

          if (!found) {
            this.notFound.set(true);
            this.loading.set(false);
            return;
          }
          if (!found.is_bundle && found.accessible && !found.buyable) {
            // owned/free course with nothing left to buy — go straight to content.
            // Bundles have no content of their own, so they always stay on detail.
            const lang = this.i18n.getLanguage() || 'en';
            this.router.navigate([`/${lang}/course`, found.course_ref], { replaceUrl: true });
            return;
          }
          // Otherwise show the detail page: not-owned (buy), or owned + repurchasable
          // (view AND buy again).
          this.course.set(found);
          this.loading.set(false);
        },
        error: () => {
          this.notFound.set(true);
          this.loading.set(false);
        }
      });
  }

  buy(): void {
    const course = this.course();
    const lang = this.i18n.getLanguage() || 'en';
    if (!course?.product_id) return;
    this.router.navigate([`/${lang}/order-wizard`], {
      queryParams: { add: course.product_id, step: 2 }
    });
  }

  open(): void {
    const course = this.course();
    const lang = this.i18n.getLanguage() || 'en';
    if (!course) return;
    this.router.navigate([`/${lang}/course`, course.course_ref]);
  }

  back(): void {
    const lang = this.i18n.getLanguage() || 'en';
    this.router.navigate([`/${lang}/program-catalog`]);
  }
}
